import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {getPictureRequest, listAllPicturesRequest} from 'util/api';
import {WebSocketContext} from 'wss/WebSocketService';
import TrendingCalculator from 'util/trendingCalculator';

const channelName = 'picture';

export const PicturesContext = createContext();

class Picture {
    constructor(data, currentTime) {
        this.id = data.id;
        this.authorName = data.authorName;
        this.authorProfileImgId = data.authorProfileImgId;
        this.authorId = data.authorId;
        this.authorProfileUrl = data.authorProfileUrl;
        this.height = data.height;
        this.purchases = data.purchases || [];
        this.lastAuthorPayment = data.lastAuthorPayment;
        this.url = data.url;
        this.width = data.width;
        this.commentCount = data.commentCount;
        this.trending = TrendingCalculator.calculateImageTrending(this.purchases, currentTime);

        // Calculate createdOn from the earliest payment time
        this.createdOn = this.purchases.length > 0
            ? Math.min(...this.purchases.map(purchase => purchase.paidOn))
            : currentTime; // Default to current time if no purchases
    }

    getAmount() {
        return this.purchases.reduce((total, purchase) => total + purchase.amount, 0);
    }

    calculateTrending(currentTime) {
        return this.purchases.reduce((total, purchase) => {
            const timeMinutes = (currentTime - purchase.paidOn) / (60 * 1000);
            return total + (purchase.amount * 1000) / (timeMinutes + 1);
        }, 0);
    }
}

export const PicturesStateProvider = ({children}) => {
    const wsService = useContext(WebSocketContext);
    const picturesMapRef = useRef(new Map());
    const [allPictures, setAllPictures] = useState([]);
    const [sortedByAmount, setSortedByAmount] = useState([]);
    const [sortedByLastAuthorPayment, setSortedByLastAuthorPayment] = useState([]);
    const [sortedByTrending, setSortedByTrending] = useState([]);

    const isOnMessageBindRef = useRef(false);

    useEffect(() => {
        const fetchPicturesData = async () => {
            try {
                const picturesData = await listAllPicturesRequest();
                const currentTime = Math.floor(Date.now());
                const picturesMap = new Map();
                picturesData.forEach((data) => {
                    const picture = new Picture(data, currentTime);
                    picturesMap.set(picture.id, picture);
                });
                picturesMapRef.current = picturesMap;

                const allPicturesArray = Array.from(picturesMap.values());
                setAllPictures(allPicturesArray);

                // Initialize sorted lists
                setSortedByAmount([...allPicturesArray].sort((a, b) => {
                    const amountDiff = b.getAmount() - a.getAmount();
                    if (amountDiff !== 0) {
                        return amountDiff;
                    }
                    return b.createdOn - a.createdOn;
                }));
                console.log('soreted by amount: ', allPicturesArray.map((pic) => ({id: pic.id})));
                setSortedByLastAuthorPayment([...allPicturesArray].sort((a, b) => b.lastAuthorPayment - a.lastAuthorPayment));
                setSortedByTrending([...allPicturesArray].sort((a, b) => b.trending - a.trending));
            } catch (error) {
                console.error('Error fetching pictures data:', error);
            }
        };

        fetchPicturesData();
    }, []);

    const reorder = () => {
        console.log('Reordering pictures');
        const allPicturesArray = Array.from(picturesMapRef.current.values());
        setSortedByAmount([...allPicturesArray].sort((a, b) => {
            const amountDiff = b.getAmount() - a.getAmount();
            if (amountDiff !== 0) {
                return amountDiff;
            }
            return b.createdOn - a.createdOn;
        }));
        setSortedByLastAuthorPayment([...allPicturesArray].sort((a, b) => b.lastAuthorPayment - a.lastAuthorPayment));
        setSortedByTrending([...allPicturesArray].sort((a, b) => b.trending - a.trending));
    };

    useEffect(() => {
        const handleMessage = (content) => {
            console.log('Received message on pictures channel:', content);

            const {pictureId, authorFund, fundAmount, fundOn} = content;
            const currentTime = Date.now();

            const picturesMap = picturesMapRef.current;
            const picture = picturesMap.get(pictureId);
            if (picture) {
                // Update the picture's data
                picture.lastAuthorPayment = authorFund ? currentTime : picture.lastAuthorPayment;
                picture.purchases.push({
                    amount: fundAmount,
                    paidOn: fundOn,
                });
                // Recalculate trending
                picture.trending = picture.calculateTrending(currentTime);

                // Force a re-render without changing the order
                setAllPictures((prevPictures) => [...prevPictures]);
            } else {
                getPictureRequest(pictureId).then((data) => {
                    const picture = new Picture(data, currentTime);
                    picturesMap.set(picture.id, picture);
                    setAllPictures((prevPictures) => [...prevPictures, picture]);
                });
            }
        };

        if (wsService) {
            wsService.connect();
            wsService.bindChannel(channelName);
            if (!isOnMessageBindRef.current) {
                isOnMessageBindRef.current = true;
                wsService.onMessage(channelName, handleMessage);
            }
        }

        return () => {
            wsService.unbindChannel(channelName);
        };
    }, [wsService]);

    // Crawler to recalculate trending every 1 second
    useEffect(() => {
        const intervalId = setInterval(() => {
            const currentTime = Date.now();
            const picturesMap = picturesMapRef.current;

            // Recalculate trending for each picture
            picturesMap.forEach((picture) => {
                picture.trending = picture.calculateTrending(currentTime);
            });

            // Force a re-render without changing the order
            setAllPictures((prevPictures) => [...prevPictures]);
        }, 1000);

        return () => {
            clearInterval(intervalId);
        };
    }, []);

    return (
        <PicturesContext.Provider
            value={{
                allPictures,
                sortedByAmount,
                sortedByLastAuthorPayment,
                sortedByTrending,
                reorder
            }}
        >
            {children}
        </PicturesContext.Provider>
    );
};
